﻿export const heroSlides = () => {
  /**
   * Gets all CSS classes for active elements from the specified container
   * @param slidesContainer The element containing the 'data-active-slide-css-classes' attribute
   * @returns String array where each entry is a CSS class
   */
  const getActiveSlideCssClasses = (slidesContainer: HTMLElement) =>
    (slidesContainer.dataset.activeSlideCssClasses || "")
      .split(" ")
      .filter(x => x.length);

  /**
   * Gets all CSS classes for active elements from the specified container
   * @param slidesContainer The element containing the 'data-inactive-slide-css-classes' attribute
   * @returns String array where each entry is a CSS class
   */
  const getInactiveSlideCssClasses = (slidesContainer: HTMLElement) =>
    (slidesContainer.dataset.inactiveSlideCssClasses || "")
      .split(" ")
      .filter(x => x.length);

  /** Gets the index of the currently active slide within the specified slides container. */
  const getCurrentIndex = (slidesContainer: HTMLElement) => {
    try {
      const activeCssClasses = getActiveSlideCssClasses(slidesContainer);

      const activeSlide = slidesContainer.querySelector(
        `.${activeCssClasses.join(".")}`
      ) as HTMLElement;

      return parseInt(activeSlide.dataset.slideIndex || "-1");
    } catch (err) {
      console.error("Failed to get active slide index", err);
    }

    return -1;
  };

  /** Switches slides to the one with the specified index within the specified slides container. */
  const switchSlides = (containerElement: HTMLElement, newIndex: number) => {
    try {
      const activeCssClasses = getActiveSlideCssClasses(containerElement),
        inactiveCssClasses = getInactiveSlideCssClasses(containerElement);

      containerElement.querySelectorAll("[data-slide-index]").forEach(slide => {
        const slideElement = slide as HTMLElement;

        const slideIndex = parseInt(slideElement.dataset.slideIndex || "-1");

        // Start over if on the last slide
        if (newIndex + 1 > parseInt(containerElement.dataset.slideCount || "0")) {
          newIndex = 0;
        }

        if (slideIndex === newIndex) {
          // Make slide active
          slideElement.classList.remove(...inactiveCssClasses);
          slideElement.classList.add(...activeCssClasses);
        } else {
          // Hide slide
          slideElement.classList.remove(...activeCssClasses);
          slideElement.classList.add(...inactiveCssClasses);
        }
      });
    } catch (err) {
      console.error("Failed to switch slides", err);
    }
  };

  /** Wires up the links (bullets) to switch slides when clicked. */
  const setupSlideNavigation = () => {
    try {
      document.querySelectorAll("a[data-index]").forEach(link => {
        link.addEventListener("click", event => {
          try {
            const newIndex = parseInt(
              (event?.target as HTMLAnchorElement).dataset.index || "0"
            );

            const containerElement = link.closest(
              "[data-slides-container]"
            ) as HTMLElement;

            // Pause automatic slideshow
            playOrPause(containerElement, "pause");

            switchSlides(containerElement, newIndex);

            event.preventDefault();
          } catch (err) {
            console.error("Failed to switch slides", err);
          }
        });
      });
    } catch (err) {
      console.error("Failed to setup slides navigation", err);
    }
  };

  /** Starts automatic slide switching at an interval, if applicable. */
  const startSlideshow = (container?: HTMLElement | undefined) => {
    try {
      const start = (containerElement: HTMLElement) => {
        const timeoutInSeconds = parseInt(
          containerElement.dataset.autoplayInterval || "0"
        );

        if (timeoutInSeconds === 0) {
          return;
        }

        if (typeof containerElement.dataset.timer === "string") {
          console.warn(
            "Slideshow already playing",
            typeof containerElement.dataset.timer
          );

          return;
        }

        containerElement.dataset.timer = setInterval(() => {
          const newIndex = getCurrentIndex(containerElement) + 1;

          if (newIndex >= 0) {
            switchSlides(containerElement, newIndex);
          }
        }, timeoutInSeconds * 1000).toString();
      };

      if (container) {
        start(container);
      } else {
        document.querySelectorAll("[data-slides-container]").forEach(container => {
          start(container as HTMLElement);
        });
      }
    } catch (error) {
      console.error("Failed setting up slideshow", error);
    }
  };

  /** Starts or stops automatic slide switching. */
  const playOrPause = (containerElement: HTMLElement, action: "play" | "pause") => {
    const playButtons = containerElement.querySelectorAll("[data-slide-command=play]");

    const pauseButtons = containerElement.querySelectorAll("[data-slide-command=pause]");

    if (action === "play") {
      // Immediately switch slides and then start autoplay, if applicable
      const newIndex = getCurrentIndex(containerElement) + 1;
      switchSlides(containerElement, newIndex);
      startSlideshow(containerElement);
    } else if (action === "pause") {
      clearInterval(parseInt(containerElement.dataset.timer || ""));
      delete containerElement.dataset.timer;
    }

    playButtons.forEach(btn => {
      if (action === "play") {
        btn.classList.add("hidden");
      } else {
        btn.classList.remove("hidden");
      }
    });

    pauseButtons.forEach(btn => {
      if (action === "pause") {
        btn.classList.add("hidden");
      } else {
        btn.classList.remove("hidden");
      }
    });
  };

  /** Positions play controls and scroll indicator on browser resize and scrolling */
  const setupControlsPositioning = () => {
    const reposition = () => {
      try {
        let containerRect: DOMRect | undefined;

        let playControlsRect: DOMRect | undefined;

        document.querySelectorAll("[data-play-controls]").forEach(elm => {
          const playControls = elm as HTMLElement;

          const slideContainer = elm.closest("[data-slide-container]") as HTMLElement;

          const slideIndex = slideContainer.dataset.slideIndex;

          if (!containerRect) {
            containerRect = slideContainer.getBoundingClientRect();
          }

          if (!playControlsRect) {
            playControlsRect = playControls.getBoundingClientRect();
          }

          // Place play controls at the bottom of the viewport or the bottom of the hero slide, whichever comes first

          const marginFromBottom = 46;

          let bottomPosition =
            Math.min(window.innerHeight, containerRect.bottom) -
            containerRect.top -
            playControlsRect.height -
            marginFromBottom;

          // don't allow negative positions
          bottomPosition = Math.max(marginFromBottom, bottomPosition);

          playControls.classList.remove("opacity-0");

          const slideContent = slideContainer.querySelector("[data-slide-content]") as
            | HTMLElement
            | undefined;

          if (!slideContent) {
            console.warn(
              `Slide with index ${slideIndex} does not have a content element, i.e. element with a data-slide-content attribute, meaning we can't determine if play controls overlap.'.`
            );
          } else {
            const slideContentRect = slideContent.getBoundingClientRect();

            const marginOfSafety = 10;

            const belowSlideContent =
              slideContent.offsetTop + slideContentRect.height + marginOfSafety;

            const wouldCollide = belowSlideContent > bottomPosition;

            if (wouldCollide) {
              // Play controls would overlap with slide content.
              // Lock position to below the content (to avoid overlap when resizing and avoid overlap of scroll indicator in mobile)
              bottomPosition = belowSlideContent;
            }
          }

          playControls.style.top = `${bottomPosition}px`;

          // position scroll indicator using the same offset as the play controls to avoid lopsided alignment and overlap with content in mobile.
          slideContainer.querySelectorAll("[data-scroll-indicator]").forEach(elm => {
            const scrollIndicator = elm as HTMLElement;

            scrollIndicator.classList.remove("opacity-0");
            scrollIndicator.style.top = `${bottomPosition}px`;
          });
        });
      } catch (err) {
        console.error("Failed to position play controls", err);
      }
    };

    // Continue repositioning when scrolling or resizing browser
    window.addEventListener("resize", _ => reposition());

    window.addEventListener("scroll", _ => reposition());

    window.addEventListener("load", _ => reposition());
  };

  /** Sets up buttons to start/stop autoplay. */
  const setupPlayPauseButton = () => {
    try {
      document.querySelectorAll("[data-slides-container]").forEach(container => {
        const containerElement = container as HTMLElement;

        const playButtons = containerElement.querySelectorAll(
          "[data-slide-command=play]"
        );

        playButtons.forEach(btn => {
          btn.addEventListener("click", evt => {
            playOrPause(containerElement, "play");
            evt.preventDefault();
          });
        });

        const pauseButtons = containerElement.querySelectorAll(
          "[data-slide-command=pause]"
        );

        pauseButtons.forEach(btn => {
          btn.addEventListener("click", evt => {
            playOrPause(containerElement, "pause");
            evt.preventDefault();
          });
        });
      });
    } catch (error) {
      console.error("Failed setting up play/pause button", error);
    }
  };

  setupSlideNavigation();

  setupPlayPauseButton();

  setupControlsPositioning();

  startSlideshow();
};
